<template>
  <div>
    <b-row class="table-view_row">
      <b-col class="table-view_row_col wide col-4">
        <b-overlay :show="show" rounded="sm">
        <div v-if="editMode" class="table-view_row_col__field editing align-items-center">
          <b-input-group>
            <b-form-input
            class="mb-0 mr-2"
              v-model="localName"
              :placeholder="fieldsLookupResult.name"
              @keyup.enter="saveFieldNameAi"
            ></b-form-input>
            <b-input-group-append>
              <b-btn variant="outline-secondary" size="sm" @click="saveFieldNameAi" class="btn-image-prepend">
                <img src="/images/sparks.png" alt="Mynd - Ai">
              </b-btn>
            </b-input-group-append>
          </b-input-group>
          <b-button-group>
            <b-btn variant="outline-secondary" size="sm" @click="saveFieldName"
              ><i class="fa fa-save"></i
            ></b-btn>
            <b-btn variant="outline-secondary" size="sm" @click="editMode = false"
              ><i class="fa fa-times"></i
            ></b-btn>
            <b-btn variant="outline-secondary" size="sm" @click="clearFieldName"
              ><i class="fa fa-undo"></i
            ></b-btn>
          </b-button-group>
        </div>

        <div v-if="!editMode" class="table-view_row_col__field align-items-center">
          <p :id="field._id" @click="showOptions(field)">
            {{ fieldsLookupResult.name }}
          </p>
          <b-tooltip
            :target="field._id"
            :title="fieldsLookupResult.name"
          ></b-tooltip>
          <b-btn variant="outline-secondary" size="sm" class="ml-auto" @click="editMode = true"
            ><i class="fa fa-edit"></i
          ></b-btn>
        </div>

        <b-row v-for="theme in field.themes" :key="theme._id">
          <b-col cols="9" class="table-view_row_col__themes ml-0">
            <div :id="theme._id" class="theme pr-1" @click="showOptions(theme)">
              <i class="fa fa-square"></i>
            </div>
            <b-tooltip :target="theme._id" :title="theme.name"></b-tooltip>
            <div
              class="concepts mr-1"
              :class="{ opened: themeOpened === theme._id }"
            >
              <b-badge
                v-for="concept in theme.concepts"
                :key="concept.id"
                class="mr-1 mb-1"
                variant="light"
                @click="showOptions(concept)"
              >
                {{ concept.name }}
              </b-badge>
            </div>
            <div class="more ml-auto" @click="openTheme(theme._id)">
              <i
                class="fa fa-caret-down"
                :class="{ 'fa-caret-up': themeOpened === theme._id }"
              ></i>
            </div>
          </b-col>
          <b-col class="table-view_row_col__percentage text-right">
            {{ theme.percentage }}%
          </b-col>
        </b-row>
        </b-overlay>
      </b-col>

      <b-col
        class="table-view_row_col d-flex flex-column justify-content-center"
      >
        <div class="table-view_row_col__docs">{{ field.size }} Documents</div>
        <svg
          class="table-view_row_col__arrow"
          :style="getArrowPosition(field.recency)"
        >
          <path d="M46 26 L56 26 L32 0 L8 26 L18 26 L18 64 L46 64 Z"></path>
        </svg>
        <div class="table-view_row_col__docs">Recency: {{ field.recency }}</div>
      </b-col>

      <b-col class="table-view_row_col mw-350">
        <b-row class="table-view_row_col__player">
          <b-col cols="8">Player</b-col>
          <b-col cols="4">% in Field</b-col>
        </b-row>
        <b-row v-for="player in field.players.list" :key="player._id">
          <b-col
            cols="8"
            class="table-view_row_col__plkey"
            @click="showOptions(player)"
            >{{ player.name }}</b-col
          >
          <b-col cols="4" class="table-view_row_col__plval">
            {{ ((player.size / field.players.total) * 100).toFixed(2) }}%
          </b-col>
        </b-row>
      </b-col>

      <b-col class="table-view_row_col">
        <div v-if="fieldComment && !fieldNoteArea" class="table-view_row_col__notes">
          <p class="field-note">{{ fieldComment }}</p>
          <div class="mt-2">
            <b-btn size="sm" variant="outline-secondary" class="mr-1" @click="removeComment"
              ><i class="fa fa-trash"></i
            ></b-btn>
            <b-btn size="sm" variant="outline-secondary" class="mr-1" @click="addComment"
              ><i class="fa fa-edit"></i
            ></b-btn>
          </div>
        </div>
        <div v-else class="table-view_row_col__notes">
          <b-form-textarea
            v-if="fieldNoteArea"
            id="input-notes"
            v-model.trim="comment"
            class="field-note-area"
          ></b-form-textarea>
          <b-btn v-if="!fieldNoteArea" size="sm" variant="outline-secondary" @click="addComment">Add comment</b-btn>
          <b-btn v-else size="sm" variant="outline-secondary" @click="saveComment">Save comment</b-btn>
        </div>
      </b-col>

      <b-col cols="1" class="table-view_row_col d-flex justify-content-center">
        <div class="table-view_row_col__bookmarks">
          <button
            v-if="isBookmark"
            class="btn p-0 bookmarker"
            @click="removeBookmark"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-bookmark-check-fill" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M2 15.5V2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.74.439L8 13.069l-5.26 2.87A.5.5 0 0 1 2 15.5zm8.854-9.646a.5.5 0 0 0-.708-.708L7.5 7.793 6.354 6.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0l3-3z"/>
            </svg>
          </button>
          <button
            v-if="!isBookmark"
            class="btn p-0 bookmarker"
            @click="addBookmark"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-bookmark" viewBox="0 0 16 16">
              <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5V2zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1H4z"/>
            </svg>
          </button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import * as d3 from "d3";
import _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/eventBus";
import DataSettings from "@/helpers/DataSettings";

export default {
  name: "TableViewRow",
  props: {
    field: {
      type: Object,
      required: true
    },
    rawData: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      show: false,
      editMode: false,
      localName: null,
      themeOpened: null,
      comment: null,
      fieldNoteArea: false
    };
  },
  computed: {
    ...mapGetters([
      "fieldBookmarks",
      "fieldComments",
      "fieldsLookup",
      "crainDataTable",
      "modelTopic"
    ]),
    ...mapState({
      fields: state => state.field.fields,
      dataSettings: state => state.setting.dataSettings
    }),
    fieldComment() {
      return (this.fieldComments && this.fieldComments[this.field._id]) || "";
    },
    isBookmark() {
      return !!this.fieldBookmarks[this.field._id];
    },
    fieldsLookupResult() {
      return this.fieldsLookup({ id: this.field._id });
    }
  },
  methods: {
    ...mapActions([
      "changeFieldName",
      "resetFieldName",
      "removeFieldComment",
      "saveFieldBookmark",
      "removeFieldBookmark",
      "changeFieldName",
      "changeFieldNameAi",
      "loadFilters"
    ]),
    saveFieldName() {
      this.changeFieldName({
        field: this.field._id,
        name: this.localName
      }).then(res => {
        this.fieldsLookupResult.name = res.data.name;
        this.editMode = false;
        this.localName = null;
      });
    },
    saveFieldNameAi() {
      // Check if modelTopic exists or is not empty
      if (!this.modelTopic || !this.modelTopic.trim()) {
        // Show alert to set the topic
        this.$bvToast.toast("Please set a topic to continue.", {
          title: "Topic Required",
          variant: "warning",
          solid: true,
          // custom class to shift this toast lower (100px)
          toastClass: 'lower-top-100'
        });
        return;
      }
      this.show = true;
      this.changeFieldNameAi({
        topic: this.modelTopic,
        fields: [{
          id: this.field._id,
          titles: this.getTitles()
        }]
      }).then(res => {
        this.fieldsLookupResult.name = res.data.name;
        this.editMode = false;
        this.localName = null;
        this.show = false;
      });
    },
    getTitles() {
      let titles = [];
      for (let i = 0; i < this.field.themes.length; i++) {
        if (this.field.themes[i].percentage >= 20) {
          titles = [...titles, ...this.field.themes[i].name.split(',')];
        }
      }
      return titles;
    },
    clearFieldName() {
      this.resetFieldName({
        field: this.field._id
      }).then(res => {
        this.fieldsLookupResult.name = res.data.name;
        this.editMode = false;
        this.localName = null;
      });
    },
    showOptions(item) {
      EventBus.$emit("showOptions", item);
    },
    openTheme(themeId) {
      if (this.themeOpened === themeId) {
        this.themeOpened = null;
      } else {
        this.themeOpened = themeId;
      }
    },
    addBookmark() {
      let bookmarks = this.dataSettings.fields.fieldBookmarks;
      bookmarks[this.field._id] = "Bookmark";
      DataSettings.setSettings("fields.fieldBookmarks", bookmarks);
      this.loadFilters();
    },
    removeBookmark() {
      let bookmarks = this.dataSettings.fields.fieldBookmarks;
      delete bookmarks[this.field._id];
      DataSettings.setSettings("fields.fieldBookmarks", bookmarks);
      this.loadFilters();
    },
    addComment() {
      this.comment = this.fieldComment;
      this.fieldNoteArea = true;
    },
    saveComment() {
      let comments = this.dataSettings.fields.fieldComments;
      comments[this.field._id] = this.comment;
      DataSettings.setSettings("fields.fieldComments", comments);
      this.loadFilters();
      this.comment = null;
      this.fieldNoteArea = false;
    },
    removeComment() {
      let comments = this.dataSettings.fields.fieldComments;
      delete comments[this.field._id];
      DataSettings.setSettings("fields.fieldComments", comments);
      this.loadFilters();
    },
    getArrowPosition(rec) {
      if (rec === "-infinity" || rec < -3) {
        rec = -3;
      }
      if (rec === "infinity" || rec > 3) {
        rec = 3;
      }
      const arrowColor = d3
        .scaleLinear()
        .domain([-2, 1, 0, 1, 2])
        .range(["#710193", "#2fffe7", "#333333", "#F07E2A", "#E15109"])
        .clamp(true)
        .interpolate(d3.interpolateRgb);
      const angle = -rec * 30 + 90;
      return `transform: rotate(${angle}deg); fill: ${arrowColor(rec)}`;
    },
    getRingSize(field, ring) {
      const crainData = this.crainDataTable;
      const fid = _.findIndex(crainData.xLayers, function(f) {
        return f[0]._id === field;
      });
      const rid = _.findIndex(crainData.yLayers, function(r) {
        if (r[0] !== null) {
          return r[0]._id === ring || r[0].name === ring;
        }
      });
      const key = `${fid}_${rid}_0_0`;
      let sum = 0;
      if (crainData.rawData.data[0][key]) {
        for (let i = 0; i < crainData.yLayers.length; i++) {
          const tkey = `${fid}_${i}_0_0`;
          if (crainData.rawData.data[0][tkey]) {
            sum += crainData.rawData.data[0][tkey].data.size;
          }
        }
        return (
          ((crainData.rawData.data[0][key].data.size / sum) * 100).toFixed(2) +
          "%"
        );
      } else {
        return 0;
      }
    }
  }
};
</script>
